import { useApi, API_ENDPOINTS, transformPascalToCamel } from "@/lib/api"

export interface AtsSourceInfo {
  id: number
  code: string
  name: string
  description?: string | null
  atsSystemId?: number | null
  /** The parent etl.CodeAtsSystem code (Bullhorn / Loxo / JobAdder) */
  atsSystem?: string | null
  isActive: boolean
}

// The active source rarely changes within a session; cached so every page using
// useTenantAtsSystem doesn't refetch. Invalidated when sources are created/switched.
let activeSourcePromise: Promise<AtsSourceInfo> | null = null

export function invalidateActiveAtsSource() {
  activeSourcePromise = null
}

export function useAtsSources() {
  const { mainApi, atsIntegrationApi } = useApi()

  const loadSources = async (): Promise<AtsSourceInfo[]> =>
    transformPascalToCamel(await mainApi.get<any[]>(API_ENDPOINTS.MAIN.ATS_SOURCES)) as AtsSourceInfo[]

  const loadAtsSystems = async (): Promise<AtsSourceInfo[]> =>
    transformPascalToCamel(await mainApi.get<any[]>(API_ENDPOINTS.MAIN.ATS_SYSTEMS)) as AtsSourceInfo[]

  const createSource = async (request: {
    code: string
    name: string
    description?: string
    atsSystemId: number
    makeActive: boolean
  }): Promise<void> => {
    await mainApi.post(API_ENDPOINTS.MAIN.ATS_SOURCES, request)
    invalidateActiveAtsSource()
  }

  const activateSource = async (code: string): Promise<void> => {
    await mainApi.post(API_ENDPOINTS.MAIN.ATS_SOURCE_ACTIVATE(code), {})
    invalidateActiveAtsSource()
  }

  /** Lets the tenant's next request to the ATS system send its credentials again after the system rejected them. */
  const reconnectAtsSystem = async (atsSystem: string): Promise<void> => {
    await atsIntegrationApi.post(API_ENDPOINTS.ATS_INTEGRATION.RECONNECT(atsSystem), {})
  }

  const loadActiveSource = (): Promise<AtsSourceInfo> => {
    if (!activeSourcePromise) {
      activeSourcePromise = mainApi
        .get<any>(API_ENDPOINTS.MAIN.ATS_ACTIVE_SOURCE)
        .then((response) => transformPascalToCamel(response) as AtsSourceInfo)
        .catch((err) => {
          // Don't cache failures
          activeSourcePromise = null
          throw err
        })
    }
    return activeSourcePromise
  }

  return { loadSources, loadAtsSystems, createSource, activateSource, reconnectAtsSystem, loadActiveSource }
}
